import { useId, useState } from 'react';
import styled from 'styled-components';

import { Checkbox } from '../../../cdl/Checkbox/Checkbox';
import { Collapsable } from '../../../cdl/Collapsable/Collapsable';
import { translate } from '../../../common/helpers/translate.helper';
import { IconChevronDown } from '../../../common/icons/cdl/ChevronDown';
import { IconChevronUp } from '../../../common/icons/cdl/ChevronUp';
import { Box } from '../../../common/ui/Box';
import { theme } from '../../../common/ui/theme';
import { isSameProductKey } from '../../isSameProductKey';
import type { DemandCalculation } from '../../types/DemandCalculation';
import type { DemandProductCategoryOption } from '../../types/DemandProductCategoryOption';
import type { DemandProductKey } from '../../types/DemandProductKey';
import { DemandHistoricalVolume } from '../DemandHistoricalVolume';

import { ProductKeyRow } from './ProductKeyRow';

const ChevronTrigger = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    height: ${theme.space[24]};
    padding: 0;
    border: none;
    border-radius: ${theme.radii[4]};
    background: transparent;
    color: ${theme.colors.foreground.subtle};
    cursor: pointer;
    transition:
        background-color 100ms ease-out,
        color 100ms ease-out;

    &:hover {
        background: ${theme.colors.background.subtle};
        color: ${theme.colors.foreground.default};
    }

    &:focus-visible {
        outline: none;
        box-shadow: ${theme.shadows.focus};
    }
`;

interface ProductCategoryRowProps {
    category: DemandProductCategoryOption;
    excludedProductKeys: DemandProductKey[];
    historicalVolume?: number;
    isHistoricalVolumeLoading: boolean;
    isSelected: boolean;
    onChange: (category: DemandProductCategoryOption, checked: boolean) => void;
    onProductChange: (category: DemandProductCategoryOption, productKey: DemandProductKey, checked: boolean) => void;
    products: DemandCalculation['productKeyVolumes'];
    showHistoricalData: boolean;
}

export const ProductCategoryRow = ({
    category,
    excludedProductKeys,
    historicalVolume,
    isHistoricalVolumeLoading,
    isSelected,
    onChange,
    onProductChange,
    products,
    showHistoricalData,
}: ProductCategoryRowProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const productListId = useId();
    const selectedProducts = products.filter(
        (product) => !excludedProductKeys.some((excludedKey) => isSameProductKey(excludedKey, product.productKey))
    );
    const isFullySelected = isSelected && (products.length === 0 || selectedProducts.length === products.length);
    const isIndeterminate = isSelected && selectedProducts.length > 0 && selectedProducts.length < products.length;
    const Icon = isExpanded ? IconChevronUp : IconChevronDown;
    const headerColumns = showHistoricalData ? 'minmax(0, 1fr) 140px 24px' : 'minmax(0, 1fr) 24px';

    return (
        <Box gridColumn="1 / -1">
            <Collapsable
                isOpen={isExpanded && products.length > 0}
                header={
                    <Box
                        display="grid"
                        gridTemplateColumns={headerColumns}
                        columnGap={8}
                        alignItems="center"
                        paddingY={8}
                    >
                        <Checkbox
                            checked={isFullySelected}
                            indeterminate={isIndeterminate}
                            label={category.label}
                            truncateLabel
                            onChange={(event) => onChange(category, event.target.checked)}
                        />
                        {showHistoricalData ? (
                            <DemandHistoricalVolume
                                color="foreground.default"
                                historicalVolume={historicalVolume}
                                isLoading={isHistoricalVolumeLoading}
                            />
                        ) : null}
                        {products.length > 0 ? (
                            <ChevronTrigger
                                type="button"
                                aria-label={translate(
                                    isExpanded
                                        ? 'demandDefinition.hideProductsInCategory'
                                        : 'demandDefinition.showProductsInCategory',
                                    { category: category.label }
                                )}
                                aria-expanded={isExpanded}
                                aria-controls={productListId}
                                onClick={() => setIsExpanded((expanded) => !expanded)}
                            >
                                <Icon width={16} height={16} />
                            </ChevronTrigger>
                        ) : (
                            <Box />
                        )}
                    </Box>
                }
            >
                {products.length > 0 ? (
                    <Box id={productListId} display="flex" flexDirection="column" paddingTop={4} paddingBottom={8}>
                        {products.map((product) => (
                            <ProductKeyRow
                                key={`${product.productKey.type}-${product.productKey.value}`}
                                product={product}
                                isSelected={isSelected && selectedProducts.includes(product)}
                                showHistoricalData={showHistoricalData}
                                onChange={(productKey, checked) => onProductChange(category, productKey, checked)}
                            />
                        ))}
                    </Box>
                ) : null}
            </Collapsable>
        </Box>
    );
};
